import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Stack, TextField, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import BtnPrimary from '../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import FormField from '../../../../../../component/Formfield';
import SectionLoader from '../../../../../../component/SectionLoader';
import { FormSection, FieldGrid, FieldLabel, WizardActions } from '../../../../../../component/wizard';
import { getOrgDepartments, getOrgEmployees } from '../../../../../../services/globalData';
import { useNotify } from '../../../../../../component/NotificationProvider';
import AddNewOs from './AddNewOs';
import { addMachine, updateMachine, getAssetById, getVendors } from "../../../../../../services/asset";
import { tokens } from '../../../../../../theme';
import RecordField from '../../../../../../component/RecordField';
import AssetTransferHistory from '../AssetTransferHistory';
import { getLegalUnitList } from '../../../../../../services/globalData';
import { StatusChip } from '../../../../../../component/listing';

import { displayFont } from "../../../../../../theme";
const dateInputSx = {
  width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: "0.875rem", color: tokens.text,
  border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "11px 14px",
  background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` },
};
const acSx = {
  '& .MuiInputBase-root': { height: '3rem', borderRadius: '10px', fontSize: '0.9375rem' },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
};
const toInput = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
/** "14 September 2026" — spelled from a table; ICU renders "Sept" for September. */
const longDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

const MachineForm = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { state } = useLocation();
  const [mode, setMode] = useState(state?.mode === "view" ? "view" : "create");
  const [assetId] = useState(state?.assetId || null);
  const [loading, setLoading] = useState(Boolean(state?.assetId));
  const [apiCall, setApiCall] = useState(false);
  const [addNewOs, setAddNewOs] = useState(false);
  const [form, setForm] = useState({
    type: "", name: "", manufacturer: "", operatingSystem: "", model: "",
    serialNumber: "", vendor: "", activeServiceDate: "", status: "active", employee: "", department: "",
  });
  const [errors, setErrors] = useState({});
  // active only: a retired supplier keeps its link on existing assets but should
  // not be offered for a new one
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    getVendors(true)
      .then((res) => setVendors(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {}); // the rest of the form still works without the list
  }, []);

  const departments = (getOrgDepartments() || []).map((d) => ({ name: d.name, id: d._id }));
  const employees = (getOrgEmployees() || []).map((e) => ({ name: `${e.fname} ${e.lname}`, id: e._id }));
  const readOnly = mode === "view";
  // Which company's books this asset sits on. Read from the cached entity list
  // rather than populated by the API, which keeps the asset payload unchanged.
  const entityName =
    (getLegalUnitList() || []).find((u) => String(u._id) === String(form.legalUnit))?.name ||
    "No entity yet";

  useEffect(() => {
    if (!state?.assetId) return;
    let active = true;
    (async () => {
      try {
        const res = await getAssetById(state.assetId);
        if (res?.success === false) throw apiError(res, "Failed to load machine");
        const a = res?.data || {};
        if (!active) return;
        setForm({
          type: a.type || "", name: a.name || "", manufacturer: a.manufacturer || "",
          operatingSystem: a.operatingSystem || "", model: a.model || "", serialNumber: a.serialNumber || "",
          vendor: a.vendor?._id || a.vendor || "", activeServiceDate: toInput(a.activeServiceDate),
          status: a.status || "active", employee: a.employee?._id || a.employee || "", department: a.department?._id || a.department || "",
          legalUnit: a.legalUnit?._id || a.legalUnit || "",
        });
      } catch (e) {
        notify.error(e?.message || "Failed to load machine");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const submit = async () => {
    const e = {};
    if (!form.name) e.name = "Name is required";
    if (!form.type) e.type = "Type is required";
    if (Object.keys(e).length) { setErrors(e); return; }

    setApiCall(true);
    try {
      const res = assetId ? await updateMachine(assetId, form) : await addMachine(form);
      if (res?.success === false) throw apiError(res, "Could not save machine");
      notify.success(assetId ? "Machine updated" : "Machine added");
      navigate("/app/machines");
    } catch (err) {
      notify.error(err?.message || "Something went wrong saving the machine");
    } finally {
      setApiCall(false);
    }
  };

  const title = mode === "create" ? "Add Machine" : mode === "edit" ? "Edit Machine" : form.name || "Machine";
  const empValue = employees.find((x) => x.id === form.employee) || null;
  const deptValue = departments.find((x) => x.id === form.department) || null;

  return (
    /*
      No TopBar and no WizardCard: the parent route already draws the page
      header and the card this sits in, so rendering both again gave the screen
      two headings and three nested card grounds.
    */
    <Box sx={{ width: "100%" }}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{title}</Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary to="/app/machines" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
            {readOnly && <BtnPrimary onClick={() => setMode("edit")} sx={{ borderRadius: "8px" }}>Edit</BtnPrimary>}
          </Stack>
        </Stack>

        {loading ? <SectionLoader /> : (
          <>
            {/*
              A record being read is label and value, not a disabled form. The
              edit controls below are the same fields with `readOnly` off, so
              there is one definition of what a machine holds.
            */}
            {readOnly ? (
              <>
                <FormSection title="Machine details">
                  <FieldGrid>
                    <RecordField label="Name" value={form.name} />
                    <RecordField label="Type" value={form.type} />
                    <RecordField label="Operating system" value={form.operatingSystem} />
                    <RecordField label="Manufacturer" value={form.manufacturer} />
                    <RecordField label="Model" value={form.model} />
                    {/* An identifier: read character by character and checked
                        against the sticker on the machine. */}
                    <RecordField label="Serial number" field="serialNumber" value={form.serialNumber} />
                    <RecordField label="Vendor" value={vendors.find((v) => v._id === form.vendor)?.name} />
                    <RecordField label="Active service date" value={longDate(form.activeServiceDate)} />
                    <RecordField label="Status">
                      <StatusChip
                        label={form.status === "inactive" ? "Retired" : "Active"}
                        tone={form.status === "inactive" ? "muted" : "success"}
                      />
                    </RecordField>
                  </FieldGrid>
                </FormSection>

                <FormSection title="Assignment" description="Who or which department holds this machine.">
                  <FieldGrid>
                    <RecordField label="Employee" value={empValue?.name} />
                    <RecordField label="Department" value={deptValue?.name} />
                  </FieldGrid>
                </FormSection>

                {/* Where this asset sits on the books, and how it got there.
                    Shown on the record rather than only inside the move dialog:
                    somebody asking "why is this on the other company's books?"
                    is looking at the asset, not about to move it. */}
                <FormSection
                  title="Ownership"
                  description="The legal entity that owns this asset, and every time it changed."
                >
                  <FieldGrid>
                    <RecordField label="Owned by" value={entityName} />
                  </FieldGrid>
                  <Box sx={{ mt: "1.25rem" }}>
                    <AssetTransferHistory assetId={state?.assetId} />
                  </Box>
                </FormSection>
              </>
            ) : (
              <>
            <FormSection title="Machine details">
              <FieldGrid>
                <Box>
                  <FieldLabel required>Name</FieldLabel>
                  <FormField type="text" value={form.name} validation={errors.name} disabled={readOnly} onChange={(e) => change('name', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel required>Type</FieldLabel>
                  <FormField type="text" value={form.type} validation={errors.type} disabled={readOnly} onChange={(e) => change('type', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Operating System</FieldLabel>
                  <FormField type="select" options={["windows", "mac", "linux", "other"]} value={form.operatingSystem} disabled={readOnly}
                    onChange={(e) => { change('operatingSystem', e.target.value); if (e.target.value === "other") setAddNewOs(true); }} />
                </Box>
                <Box>
                  <FieldLabel>Manufacturer</FieldLabel>
                  <FormField type="text" value={form.manufacturer} disabled={readOnly} onChange={(e) => change('manufacturer', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Model</FieldLabel>
                  <FormField type="text" value={form.model} disabled={readOnly} onChange={(e) => change('model', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Serial Number</FieldLabel>
                  <FormField type="text" value={form.serialNumber} disabled={readOnly} onChange={(e) => change('serialNumber', e.target.value)} />
                </Box>
                <Box>
                  {/* the field existed in this form's state and was hydrated
                      from the asset, with nothing to set it — so no asset could
                      ever record where it came from */}
                  <FieldLabel>Vendor</FieldLabel>
                  <Autocomplete
                    size="small"
                    disabled={readOnly}
                    options={vendors}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={vendors.find((v) => v._id === form.vendor) || null}
                    onChange={(e, v) => change('vendor', v?._id || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Where it was bought" />}
                  />
                </Box>
                <Box>
                  <FieldLabel>Active Service Date</FieldLabel>
                  <Box component="input" type="date" value={form.activeServiceDate} disabled={readOnly} onChange={(e) => change('activeServiceDate', e.target.value)} sx={dateInputSx} />
                </Box>
                <Box>
                  <FieldLabel>Status</FieldLabel>
                  <FormField type="select" options={["active", "inactive"]} value={form.status} disabled={readOnly} onChange={(e) => change('status', e.target.value)} />
                </Box>
              </FieldGrid>
            </FormSection>

            <FormSection title="Assignment" description="Who or which department holds this machine.">
              <FieldGrid>
                <Box>
                  <FieldLabel>Employee</FieldLabel>
                  <Autocomplete options={employees} getOptionLabel={(o) => o?.name || ""} size="small" disabled={readOnly} value={empValue}
                    onChange={(e, v) => change('employee', v?.id || "")} sx={acSx} renderInput={(params) => <TextField {...params} placeholder="Unassigned" />} />
                </Box>
                <Box>
                  <FieldLabel>Department</FieldLabel>
                  <Autocomplete options={departments} getOptionLabel={(o) => o?.name || ""} size="small" disabled={readOnly} value={deptValue}
                    onChange={(e, v) => change('department', v?.id || "")} sx={acSx} renderInput={(params) => <TextField {...params} placeholder="Unassigned" />} />
                </Box>
              </FieldGrid>
            </FormSection>

              </>
            )}

            {!readOnly && (
              <WizardActions>
                <BtnSecondary to="/app/machines" sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
                <BtnPrimary onClick={submit} disabled={apiCall} sx={{ borderRadius: "8px" }}>
                  {apiCall ? <CircularProgress size={"1.25rem"} /> : assetId ? "Save Changes" : "Add Machine"}
                </BtnPrimary>
              </WizardActions>
            )}
          </>
        )}
        <AddNewOs open={addNewOs} onClose={() => setAddNewOs(false)} />
      </Box>
    </Box>
  );
};

export default MachineForm;
